import i18n from '@/i18n';
/**
 * Типи й форматування розділу «Бюджет».
 *
 * Форми відповідей повторюють budget_routes.js. Усі грошові суми приходять у
 * гривнях і форматуються тут — на екран виводимо мільйони, бо бюджет станції
 * читають мільйонами, а копійки в таблиці на 12 стовпців лише шумлять.
 */

export interface BudgetVersion {
  id: string; kind: 'year' | 'month'; period: string; periodLabel: string;
  scenario: 'low' | 'base' | 'high'; stage: string; status: string; stress: boolean;
  source: string; sourceFile: string | null;
  approvedBy: string | null; approvedAt: string | null; autoApproved: boolean;
  note: string | null; createdAt: string;
}

export interface MonthRow {
  month: string;
  exportMwh: number | null; avgPrice: number | null;
  revenue: number | null; bessEffect: number | null;
  variable: number | null; opexFixed: number | null;
  ebitda: number | null; finance: number | null;
  profitPreTax: number | null; tax: number | null;
  netProfit: number | null; principal: number | null;
  cashFlow: number | null; cashFlowCum: number | null;
  fixedCosts: number | null; beRevenue: number | null;
  safetyMargin: number | null; beBasePrice: number | null;
  beExportMwh: number | null; beRevenueCash: number | null; cashMargin: number | null;
}

export interface LoanRow {
  month: string; openEur: number | null; fxRate: number | null;
  interestEur: number | null; interestUah: number | null;
  principalEur: number | null; principalUah: number | null;
  closeEur: number | null; bankPayment: number | null;
  rko: number | null; commission: number | null; debtService: number | null;
}

export interface ScenarioRow {
  month: string; baseLow: number | null; baseBase: number | null; baseHigh: number | null;
  baseSelected: number | null; exportWithBessMwh: number | null;
  captureWithBess: number | null; captureWithoutBess: number | null;
  chargeMwh: number | null; dischargeMwh: number | null; exportWithoutBessMwh: number | null;
}

export interface ParamRow { ord: number; key: string; valueNum: number | null; valueText: string | null; note: string | null }
export interface OpexRow {
  code: string; name: string; amountMonth: number | null;
  indexation: number | null; indexationFrom: string | null; payDay: number | null; note: string | null;
}
export interface ScenarioYearRow { metric: string; low: number | null; base: number | null; high: number | null }
export interface DscrRow { period: string; ebitda: number | null; debtService: number | null; dscr: number | null }

export interface YearTotals {
  exportMwh: number; revenue: number; bessEffect: number; variable: number;
  opexFixed: number; ebitda: number; finance: number; profitPreTax: number;
  tax: number; netProfit: number; principal: number; cashFlow: number;
  avgPrice: number | null; debtService: number;
}

export interface YearResp {
  assetId: string; version: BudgetVersion | null;
  months: MonthRow[]; year: YearTotals;
  loan: LoanRow[]; priceScenario: ScenarioRow[];
  params: ParamRow[]; opex: OpexRow[];
  yearByScenario: ScenarioYearRow[]; dscr: DscrRow[];
  sourceNote: string | null;
}

export interface HourRow {
  ts: string; pos: number;
  rdn: number | null; idm: number | null;
  exportKwh: number | null; pvKwh: number | null; bessKwh: number | null;
  revenue: number | null;
}

export interface MonthResp {
  assetId: string; month: string; version: BudgetVersion | null;
  plFrom: 'month' | 'year'; pl: MonthRow | null;
  hours: HourRow[]; has: { price: boolean; plan: boolean };
}

// ── Формати ─────────────────────────────────────────────────────────────────
const uk = (v: number, d: number) =>
  v.toLocaleString((i18n.language === 'en' ? 'en-GB' : 'uk-UA'), { minimumFractionDigits: d, maximumFractionDigits: d });

/** Мільйони гривень: 15 316 000 → «15,32». */
export const mln = (v: number | null | undefined, d = 2) =>
  v == null ? '—' : uk(v / 1e6, d);
/** Ціле з розрядами. */
export const n0 = (v: number | null | undefined) => (v == null ? '—' : uk(v, 0));
export const n1 = (v: number | null | undefined) => (v == null ? '—' : uk(v, 1));
export const n2 = (v: number | null | undefined) => (v == null ? '—' : uk(v, 2));
/** Частка → відсотки: 0,7397 → «74,0%». */
export const pct = (v: number | null | undefined, d = 1) =>
  v == null ? '—' : `${uk(v * 100, d)}%`;

const MONTHS_SHORT = ['ak_bud_jan', 'ak_bud_feb', 'ak_bud_mar', 'ak_bud_apr', 'ak_bud_may', 'ak_bud_jun', 'ak_bud_jul', 'ak_bud_aug', 'ak_bud_sep', 'ak_bud_oct', 'ak_bud_nov', 'ak_bud_dec'];
/** '2026-09' → «вер 26». */
export const monthShort = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return m >= 1 && m <= 12 ? `${i18n.t(MONTHS_SHORT[m - 1])} ${String(y).slice(2)}` : ym;
};

export const SCENARIO_LABEL: Record<string, string> = {
  get low() { return i18n.t('ak_bud_low_2'); },
  get base() { return i18n.t('ak_bud_base_2'); },
  get high() { return i18n.t('ak_bud_high_2'); },
};

// ── Ф2: план-факт і точність ────────────────────────────────────────────────
export interface PlanFactDay {
  day: string; factSource: 'delivered' | 'telemetry' | null;
  planMmsKwh: number | null; planBudgetKwh: number | null; factKwh: number | null;
  deltaPct: number | null; priceFact: number | null;
  planRevenue: number | null; factRevenue: number | null; deltaRevenue: number | null;
}
export interface PlanFactResp {
  assetId: string; month: string; scenario: string;
  days: PlanFactDay[];
  mtd: {
    daysWithFact: number; factKwh: number; factRevenue: number;
    comparable: {
      days: number; planKwh: number; factKwh: number;
      planRevenue: number; factRevenue: number;
      deltaPct: number | null; deltaRevenue: number;
    };
  };
  budgetMonth: { exportMwh: number; revenue: number } | null;
  forecast: {
    method: string; exportMwh: number; revenue: number;
    daysLeft: number; deltaToBudget: number | null;
  } | null;
  thresholds: { ok: number; warn: number };
}

export interface MetricBlock {
  horizon: string; planSource: string; tol: number; days: number;
  hitRate?: number | null; mape?: number | null; bias?: number | null;
  nmae?: number | null; corrProfile?: number | null;
  hours?: number; nmaeBase?: number | null;
  profileComparable?: boolean; profileNote?: string;
}
export interface PriceZone { planAvg: number; factAvg: number; pctErr: number | null; hours: number }
export interface RevenueDecomp {
  hours: number; planUah: number; factUah: number; deltaUah: number;
  volumeEffectUah: number; profileEffectUah: number; priceEffectUah: number;
  planMwh: number; factMwh: number;
  planWeightedPrice: number; factWeightedPrice: number | null;
  residualUah: number;
}
export interface MeterBalance {
  days: number | null;
  pvMwh: number | null; pvMeters: number;
  bessDischargeMwh: number | null; bessMeters: number;
  deliveredMwh: number | null; deliveredBackupMwh: number | null; verificationMwh: number | null;
  residualMwh: number | null;
  chargeCoeffUpper: number | null; dogmaChargeCoeff: number;
}
export interface AccuracyResp {
  assetId: string; month: string; scenario: string; assetName: string | null;
  generation: MetricBlock; price: MetricBlock;
  priceZones: { dip: PriceZone | null; peak: PriceZone | null };
  revenue: RevenueDecomp | null;
  bess: { chargeMwh: number; dischargeMwh: number; hours: number; source: string } | null;
  meterBalance: MeterBalance | null;
  topErrors: { day: string; plan: number | null; fact: number | null; pctErr: number; factSource: string | null }[];
  targets: {
    generation: { hitRate: number; mape: number; nmae: number; bias: number; corrProfile: number };
    price: { hitRate: number; mape: number; nmae: number; bias: number; corrProfile: number };
  };
  factDefinition: string;
}

// ── Ф4: каса, банк ──────────────────────────────────────────────────────────
export interface CashItem {
  dueDate: string; kind: 'inflow' | 'outflow'; code: string; name: string;
  amount: number; detail?: string; basis?: string | null;
  balanceAfter?: number | null; belowBuffer?: boolean; assumedPayDay?: boolean;
}
export interface BankLine {
  id: number; op_date: string; amount: number; currency: string;
  counterparty: string | null; purpose: string | null; doc_no: string | null;
  balance_after: number | null;
  budget_item: string | null; due_date: string | null;
  confidence: number | null; status: string | null;
  confirmed_by: string | null; match_note: string | null;
}
export interface CashResp {
  assetId: string; month: string; scenario: string;
  items: CashItem[];
  opening: number | null; openingSource: string;
  closing: number | null; inflow: number; outflow: number; net: number;
  buffer: {
    needed: number; base: number; big: number; bigTriggered: boolean;
    breaches: { dueDate: string; code: string; balance: number }[];
  };
  assumptions: {
    opexPayDayAssumed: boolean; opexDefaultPayDay: number;
    taxIncluded: boolean; taxNote: string;
  };
  bank: {
    lines: BankLine[]; count: number; factInflow: number; factOutflow: number;
    unmatched: number; toConfirm: number;
  };
}
